import React from 'react'
import cx from 'classnames'
import { FormattedMessage } from 'react-intl'
import { Waypoint } from 'react-waypoint'
import { Grid } from 'components/Grid'
import RentalsListCell from 'components/rentals/RentalsListCell'
import Loader from 'components/Loader'
import Input from 'components/Input'
import { SECTION_HOST, rowHeight, sizeToGridHeight } from 'containers/Rentals/specs'
import Icon from 'components/Icon'
import messages from './messages'
import styles from './RentalsList.scss'

interface Column {
  key: string
  isSortable?: boolean
  [key: string]: any
}

interface Item {
  id: string
  [key: string]: any
}

interface RentalsListProps {
  listType?: string
  isGridLoading?: boolean
  isLoadingNextBatch?: boolean
  height?: number
  columns?: Column[]
  items: Item[]
  selectedIds?: string[]
  highlightedItemId?: string
  onScroll: () => void
  onSelectAll: (isSelected: boolean) => void
  onItemSelect: (id: string) => void
  onRowClick?: (item: Item) => void
  onSortClick?: (sort: string) => void
  activeSort?: string
}

export default function RentalsList(props: RentalsListProps) {
  const {
    listType = SECTION_HOST,
    isGridLoading = false,
    isLoadingNextBatch = false,
    height = sizeToGridHeight.small,
    columns = [],
    items,
    selectedIds = [],
    highlightedItemId,
    onScroll,
    onSelectAll,
    onItemSelect,
    onRowClick = () => {},
    onSortClick = () => {},
    activeSort = 'numberOfListings',
  } = props

  const isAllSelected = selectedIds.length > 0 && selectedIds.length === items.length
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length !== items.length

  const handleSelectAllClick = () => {
    if (isSomeSelected) {
      onSelectAll(false)
      return
    }

    onSelectAll(!isAllSelected)
  }

  const RentalsListHeaderCell = ({ cell: { key, isSortable = false } }: { cell: Column }) => (
    <div className={cx(styles.headerCell, styles[key])}>
      <h5 className={styles.secondaryTitle}>
        <FormattedMessage {...messages.columns[key]} />
        {isSortable && (
          <Icon
            name='sort'
            size='regular'
            className={cx(styles.icon, {
              [styles.isActive]: key === activeSort,
            })}
          />
        )}
      </h5>
      {key === 'select' && (
        <Input type='checkbox' checked={isAllSelected} allowDeselect={isSomeSelected} onChange={handleSelectAllClick} />
      )}
    </div>
  )

  return (
    <div className={styles.container}>
      <Grid
        isLoading={isGridLoading}
        withGroupHeaders
        withHeaders
        fixedRowCount={2}
        columns={columns}
        rowHeight={rowHeight}
        height={height}
        rows={items}
        onCellClick={(columnIndex: number, item: Item) => columnIndex !== 0 && onRowClick(item)}
        selectedRows={selectedIds}
        highlightedRow={highlightedItemId}
        className={styles.grid}
        innerClassName={styles.gridInner}
        onHeaderClick={onSortClick}
      >
        {(item: Item, column: Column, rowIndex: number, isHeader = false) =>
          isHeader ? (
            <RentalsListHeaderCell cell={column} />
          ) : (
            <RentalsListCell
              listType={listType}
              item={item}
              cellName={column.key}
              isChecked={column.key === 'select' && selectedIds.includes(item.id)}
              onSelect={onItemSelect}
            >
              {column.key === 'select' && rowIndex === items.length - 10 && <Waypoint onEnter={onScroll} />}
            </RentalsListCell>
          )
        }
      </Grid>
      {isLoadingNextBatch && <Loader type='scrolling' className={styles.loader} />}
    </div>
  )
}
